import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ImageUploader } from "@/components/ImageUploader";
import { toast } from "sonner";
import { Loader2, Save, Eye, Download, RotateCcw } from "lucide-react";

const clone = (o) => JSON.parse(JSON.stringify(o));
function Field({ label, children }) { return <div className="space-y-1.5"><Label className="text-sm text-brown">{label}</Label>{children}</div>; }
function Card({ children }) { return <div className="bg-white rounded-2xl soft-shadow p-6 space-y-4">{children}</div>; }

export default function AdminInvoiceTemplate() {
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/invoice-template").then((r) => setT(r.data)); }, []);
  const upd = (fn) => setT((prev) => { const n = clone(prev); fn(n); return n; });

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/invoice-template", t); setT(data); toast.success("Invoice template saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const reset = () => api.get("/admin/invoice-template").then((r) => { setT(r.data); toast("Reverted to saved template"); });

  const render = async (download) => {
    try {
      const res = await api.post("/admin/invoice-template/preview", { template: t }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (download) { const a = document.createElement("a"); a.href = url; a.download = "invoice-sample.pdf"; a.click(); }
      else window.open(url, "_blank");
    } catch { toast.error("Could not render preview"); }
  };

  if (!t) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-forest" /></div>;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl text-forest tracking-tight">Invoice Template</h1>
          <p className="text-sm text-brown/60">Configure how customer invoices look. Applies to newly generated invoices.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={reset} className="rounded-full border-sage"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
          <Button variant="outline" onClick={() => render(false)} data-testid="invoice-preview-btn" className="rounded-full border-forest text-forest"><Eye className="w-4 h-4 mr-1.5" /> Preview</Button>
          <Button variant="outline" onClick={() => render(true)} data-testid="invoice-sample-btn" className="rounded-full border-forest text-forest"><Download className="w-4 h-4 mr-1.5" /> Sample PDF</Button>
          <Button onClick={save} disabled={saving} data-testid="invoice-save-btn" className="rounded-full bg-forest hover:bg-earth text-cream">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}</Button>
        </div>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="flex-wrap h-auto bg-sand mb-5">
          <TabsTrigger value="company" data-testid="inv-tab-company">Company</TabsTrigger>
          <TabsTrigger value="header" data-testid="inv-tab-header">Header & Numbering</TabsTrigger>
          <TabsTrigger value="columns" data-testid="inv-tab-columns">Columns</TabsTrigger>
          <TabsTrigger value="payment" data-testid="inv-tab-payment">Payment</TabsTrigger>
          <TabsTrigger value="footer" data-testid="inv-tab-footer">Footer</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <Card>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Company / Business Name"><Input value={t.company_name || ""} onChange={(e) => upd((n) => { n.company_name = e.target.value; })} className="rounded-xl bg-sand" data-testid="inv-company-name" /></Field>
              <Field label="Tagline"><Input value={t.tagline || ""} onChange={(e) => upd((n) => { n.tagline = e.target.value; })} className="rounded-xl bg-sand" /></Field>
            </div>
            <Field label="Company Logo"><ImageUploader value={t.logo} onChange={(url) => upd((n) => { n.logo = url; })} label="Upload logo" /></Field>
            <Field label="Address"><Input value={t.address || ""} onChange={(e) => upd((n) => { n.address = e.target.value; })} className="rounded-xl bg-sand" /></Field>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="City"><Input value={t.city || ""} onChange={(e) => upd((n) => { n.city = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="State"><Input value={t.state || ""} onChange={(e) => upd((n) => { n.state = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="PIN Code"><Input value={t.pincode || ""} onChange={(e) => upd((n) => { n.pincode = e.target.value; })} className="rounded-xl bg-sand" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Phone"><Input value={t.phone || ""} onChange={(e) => upd((n) => { n.phone = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="WhatsApp Number"><Input value={t.whatsapp || ""} onChange={(e) => upd((n) => { n.whatsapp = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Email"><Input value={t.email || ""} onChange={(e) => upd((n) => { n.email = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="Website"><Input value={t.website || ""} onChange={(e) => upd((n) => { n.website = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="GSTIN"><Input value={t.gstin || ""} onChange={(e) => upd((n) => { n.gstin = e.target.value; })} className="rounded-xl bg-sand" /></Field>
              <Field label="PAN"><Input value={t.pan || ""} onChange={(e) => upd((n) => { n.pan = e.target.value; })} className="rounded-xl bg-sand" /></Field>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="header">
          <Card>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Invoice Title"><Input value={t.invoice_title || ""} onChange={(e) => upd((n) => { n.invoice_title = e.target.value; })} className="rounded-xl bg-sand" data-testid="inv-title" /></Field>
              <Field label="Invoice Prefix"><Input value={t.invoice_prefix || ""} onChange={(e) => upd((n) => { n.invoice_prefix = e.target.value; })} className="rounded-xl bg-sand" placeholder="LNO/2026/" /></Field>
              <Field label="Next Invoice Number"><Input type="number" value={t.next_number ?? 1} onChange={(e) => upd((n) => { n.next_number = parseInt(e.target.value || "1", 10); })} className="rounded-xl bg-sand" /></Field>
            </div>
            <p className="text-xs text-brown/60">Next invoice will be numbered <b>{(t.invoice_prefix || "") + String(t.next_number ?? 1).padStart(5, "0")}</b></p>
          </Card>
        </TabsContent>

        <TabsContent value="columns">
          <Card>
            <p className="text-sm text-brown/60">Choose which columns appear in the product table on the invoice. (Qty, Price and Amount are always shown.)</p>
            {[["sku", "SKU"], ["pack_size", "Pack Size"], ["mrp", "MRP"], ["discount", "Discount"], ["tax", "Tax"]].map(([k, lbl]) => (
              <div key={k} className="flex items-center justify-between border-b border-sand pb-2">
                <Label className="text-sm">{lbl}</Label>
                <Switch checked={!!(t.columns || {})[k]} onCheckedChange={(v) => upd((n) => { n.columns = { ...(n.columns || {}), [k]: v }; })} data-testid={`inv-col-${k}`} />
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="payment">
          <div className="space-y-4">
            <Card>
              <div className="flex items-center justify-between"><Label className="text-sm font-500">Show Payment QR Code</Label>
                <Switch checked={!!t.show_qr} onCheckedChange={(v) => upd((n) => { n.show_qr = v; })} data-testid="inv-show-qr" /></div>
              {t.show_qr && <Field label="QR Code Image"><ImageUploader value={t.qr_image} onChange={(url) => upd((n) => { n.qr_image = url; })} label="Upload QR code" /></Field>}
            </Card>
            <Card>
              <div className="flex items-center justify-between"><Label className="text-sm font-500">Show Bank Details</Label>
                <Switch checked={!!t.show_bank} onCheckedChange={(v) => upd((n) => { n.show_bank = v; })} data-testid="inv-show-bank" /></div>
              {t.show_bank && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Account Name"><Input value={t.bank?.account_name || ""} onChange={(e) => upd((n) => { n.bank = { ...(n.bank || {}), account_name: e.target.value }; })} className="rounded-xl bg-sand" /></Field>
                  <Field label="Bank Name"><Input value={t.bank?.bank_name || ""} onChange={(e) => upd((n) => { n.bank = { ...(n.bank || {}), bank_name: e.target.value }; })} className="rounded-xl bg-sand" /></Field>
                  <Field label="Account Number"><Input value={t.bank?.account_number || ""} onChange={(e) => upd((n) => { n.bank = { ...(n.bank || {}), account_number: e.target.value }; })} className="rounded-xl bg-sand" /></Field>
                  <Field label="IFSC Code"><Input value={t.bank?.ifsc || ""} onChange={(e) => upd((n) => { n.bank = { ...(n.bank || {}), ifsc: e.target.value }; })} className="rounded-xl bg-sand" /></Field>
                  <Field label="Branch"><Input value={t.bank?.branch || ""} onChange={(e) => upd((n) => { n.bank = { ...(n.bank || {}), branch: e.target.value }; })} className="rounded-xl bg-sand" /></Field>
                  <Field label="Account Type"><Input value={t.bank?.account_type || ""} onChange={(e) => upd((n) => { n.bank = { ...(n.bank || {}), account_type: e.target.value }; })} className="rounded-xl bg-sand" /></Field>
                </div>
              )}
            </Card>
            <Card>
              <div className="flex items-center justify-between"><Label className="text-sm font-500">Show UPI</Label>
                <Switch checked={!!t.show_upi} onCheckedChange={(v) => upd((n) => { n.show_upi = v; })} data-testid="inv-show-upi" /></div>
              {t.show_upi && <Field label="UPI ID"><Input value={t.upi_id || ""} onChange={(e) => upd((n) => { n.upi_id = e.target.value; })} className="rounded-xl bg-sand" placeholder="name@bank" /></Field>}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="footer">
          <Card>
            <Field label="Terms & Conditions / Policies"><Textarea value={t.footer_terms || ""} onChange={(e) => upd((n) => { n.footer_terms = e.target.value; })} className="rounded-xl bg-sand" rows={3} /></Field>
            <Field label="Thank You Message"><Textarea value={t.footer_thankyou || ""} onChange={(e) => upd((n) => { n.footer_thankyou = e.target.value; })} className="rounded-xl bg-sand" rows={3} /></Field>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
